import React, { useMemo } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import { useAddProductsModalFilters } from './AddProductsModalContext';
import { Box } from '@mui/material';
import _cloneDeep from 'lodash/cloneDeep';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import ProductTableV2 from '../Generate/components/ProductTableV2';
import { useAddProductsModalStore } from './useAddProductsModalStore';
import { AddProductsModalProvider } from './AddProductsModalContext';
import { sortByLabel, sortByUnitsSold } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterModal';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { Divider } from '@mui/material';
import { AddProductFilters } from './AddProductFilters';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';

interface AddProductsModalProps {
  open: boolean;
  onClose: () => void;
  projectId?: string | null;
  variant: 'review' | 'edit';
  excludeRetailerSkus?: string[];
  setModalSelectedProducts: (selectedProducts: Set<string>) => void;
}

const AddProductsModalInner = ({
  onClose,
  projectId,
  excludeRetailerSkus,
  setModalSelectedProducts
}: {
  onClose: () => void;
  projectId?: string | null;
  variant: 'review' | 'edit';
  setModalSelectedProducts: (selectedProducts: Set<string>) => void;
  excludeRetailerSkus?: string[];
}) => {
  const theme = useStacklineTheme();
  const { appFilters } = useBeaconFilters();

  const { getOverrideAppFilters } = useAddProductsModalFilters();

  const { modalSelectedItems, toggleModalItem, setModalSelectedItems, clearModalSelection, syncWithMainStore } =
    useAddProductsModalStore();

  const overrideAppFilters = useMemo(() => {
    const modalOverride = getOverrideAppFilters();
    if (!modalOverride) {
      return appFilters;
    }

    const categoryFilters = modalOverride.categoryFilters
      ? modalOverride.categoryFilters.map((id) => +id)
      : appFilters?.categoryFilters;
    const subcategoryFilters = modalOverride.subcategoryFilters
      ? modalOverride.subcategoryFilters.map((id) => +id)
      : appFilters?.subcategoryFilters;

    return {
      categoryFilters,
      subcategoryFilters,
      segmentFilters: appFilters?.segmentFilters ?? null,
      businessUnitFilters: appFilters?.businessUnitFilters ?? null,
      retailPriceFilter: appFilters?.retailPriceFilter ?? null
    };
  }, [appFilters, getOverrideAppFilters]);

  const selectedRetailerSkus = useMemo(() => {
    return Array.from(modalSelectedItems);
  }, [modalSelectedItems]);

  const handleAdd = () => {
    // Clear the selections after adding
    const selectedItems = modalSelectedItems;
    setModalSelectedProducts(selectedItems);

    clearModalSelection();
    syncWithMainStore();
    onClose();
  };

  const handleClose = () => {
    clearModalSelection();
    onClose();
  };

  return (
    <>
      <Flex alignItems="center" className="genericModal__header">
        <Text
          variant="h4"
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          Add Products
        </Text>
        <CloseIcon
          style={{ cursor: 'pointer', width: '24px', height: '24px', marginBottom: 'auto' }}
          onClick={() => {
            handleClose();
          }}
        />
      </Flex>
      <Divider
        sx={{
          marginTop: '22px',
          position: 'relative',
          left: '-100px',
          width: '1000px'
        }}
      />

      <GenericModal.Body>
        <Box
          sx={{
            overflow: 'auto',
            overflowX: 'hidden',
            height: '497px',
            padding: '24px',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.3)'
              }
            }
          }}
        >
          <AddProductFilters />
          <Flex flexDirection="column">
            <Flex marginBottom={theme.spacing.md}>
              <Text variant="h5">Products</Text>
            </Flex>
            <Box
              width="100%"
              sx={{
                '& .MuiTableCell-root': {
                  borderBottom: '0 !important'
                },
                '& .MuiTableHead-root .MuiTableCell-head': {
                  borderBottom: `1px solid ${theme.colors.primaryGray} !important`
                },
                '& .MuiTableContainer-root': {
                  boxShadow: 'none',
                  border: 'none'
                },
                td: {
                  border: 'none !important'
                }
              }}
            >
              <ProductTableV2
                variant="modal"
                projectId={projectId}
                excludeRetailerSkus={excludeRetailerSkus}
                overrideAppFilters={overrideAppFilters}
                overrideStore={{
                  selectedItems: modalSelectedItems,
                  toggleItem: (sku: string) => toggleModalItem(sku),
                  setSelectedItems: (items: Set<string>) => setModalSelectedItems(items),
                  deselectAll: () => clearModalSelection()
                }}
              />
            </Box>
          </Flex>
        </Box>
      </GenericModal.Body>
      <GenericModal.Footer>
        <Flex justifyContent="space-between" alignItems="center">
          <SlButton onClick={clearModalSelection}>Clear</SlButton>
          <Flex gap={8}>
            <SlButton onClick={handleAdd} variant="contained" disabled={selectedRetailerSkus.length === 0}>
              Add
            </SlButton>
          </Flex>
        </Flex>
      </GenericModal.Footer>
    </>
  );
};

export const AddProductsModal = ({
  open,
  onClose,
  projectId,
  variant,
  excludeRetailerSkus,
  setModalSelectedProducts
}: AddProductsModalProps) => {
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);
  const filters = useAppSelector((state) => state.filters);
  const brandsFollowing = useAppSelector((state) => state.brandsFollowing);
  const segmentsStateOuter = useAppSelector((state) => state.segments);

  // Sort to bring checked items to the front of the collection
  const sortBySelectedFn = (a: { isSelected: boolean }, b: { isSelected: boolean }) => {
    return a.isSelected === b.isSelected ? 0 : a.isSelected ? -1 : 1;
  };

  /**
   * A list of available categories to filter on with an additional isSelected property.
   */
  const defaultCategories = _cloneDeep(categories)
    .map((category) => ({
      ...category,
      isSelected: filters.category
        ? !!filters.category.find((categoryFilteredOn) => String(categoryFilteredOn.id) === String(category.id))
        : false
    }))
    .sort(sortByUnitsSold)
    .sort(sortBySelectedFn);

  /**
   * A list of available subcategories to filter on with an additional isSelected property.
   */
  const defaultSubcategories = _cloneDeep(subCategories)
    .map((subcategory) => ({
      ...subcategory,
      isSelected: filters.subcategory
        ? !!filters.subcategory.find(
          (subcategoryFilteredOn) => String(subcategoryFilteredOn.id) === String(subcategory.id)
        )
        : false
    }))
    .sort(sortByUnitsSold)
    .sort(sortBySelectedFn);

  const handleBackdropClick = () => {
    onClose();
  };

  // Build default brands like AppFilterModal
  const parsedFilter = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return JSON.parse(params.get('filter') || 'null') || {};
    } catch (e) {
      return {} as Record<string, unknown>;
    }
  })();

  const defaultBrands = React.useMemo(() => {
    const selectedBrandIdsFromUrl: string[] = ((parsedFilter as any)?.b || []).map((item: { i: string | number }) =>
      String(item.i)
    );
    const base = (brandsFollowing || []).filter((brand: any) => brand?.id !== 0);
    return base
      .map((brand: any) => ({
        ...brand,
        id: brand.id,
        displayName: brand.displayName || brand.brandName || brand.name || String(brand.id),
        isSelected: selectedBrandIdsFromUrl.includes(String(brand.id))
      }))
      .sort(sortByLabel as any);
  }, [brandsFollowing, parsedFilter]);

  const defaultSegments = React.useMemo(() => {
    const { mySegments, teamSegments } = segmentsStateOuter;
    const combined = [...mySegments, ...teamSegments].sort((a, b) => {
      const aName = a.displayName || '';
      const bName = b.displayName || '';
      return aName.localeCompare(bName);
    });
    return combined.map((segment) => ({ id: String(segment.id), displayName: segment.displayName, isSelected: false }));
  }, [segmentsStateOuter]);

  return (
    <GenericModal
      open={open}
      onClose={handleBackdropClick}
      sx={{
        '& .MuiPaper-root': {
          width: '752px',
          height: '642px',
          borderRadius: '8px'
        }
      }}
    >
      <AddProductsModalProvider
        initialCategories={defaultCategories}
        initialSubcategories={defaultSubcategories}
        initialBrands={defaultBrands}
        initialSegments={defaultSegments}
      >
        <AddProductsModalInner
          onClose={onClose}
          projectId={projectId}
          variant={variant}
          excludeRetailerSkus={excludeRetailerSkus}
          setModalSelectedProducts={setModalSelectedProducts}
        />
      </AddProductsModalProvider>
    </GenericModal>
  );
};
